import { z } from 'zod';
// Alteration state
export var AlterationStateKey;
(function (AlterationStateKey) {
    AlterationStateKey["AlterationState"] = "alterationState";
})(AlterationStateKey || (AlterationStateKey = {}));
export const alterationStateGuard = Object.freeze({
    [AlterationStateKey.AlterationState]: z.object({
        timestamp: z.number(),
        updatedAt: z.string().optional(),
    }),
});
// Storage provider
export var StorageProvider;
(function (StorageProvider) {
    StorageProvider["LocalStorage"] = "LocalStorage";
    StorageProvider["AzureStorage"] = "AzureStorage";
    StorageProvider["S3Storage"] = "S3Storage";
    StorageProvider["GoogleStorage"] = "GoogleStorage";
})(StorageProvider || (StorageProvider = {}));
const basicConfig = {
    publicUrl: z.string().optional(),
};
export const storageProviderDataGuard = z.discriminatedUnion('provider', [
    z.object({
        provider: z.literal(StorageProvider.LocalStorage),
        rootPath: z.string(),
        ...basicConfig,
    }),
    z.object({
        provider: z.literal(StorageProvider.AzureStorage),
        connectionString: z.string(),
        container: z.string(),
        ...basicConfig,
    }),
    z.object({
        provider: z.literal(StorageProvider.S3Storage),
        endpoint: z.string().optional(),
        region: z.string().optional(),
        bucket: z.string(),
        forcePathStyle: z.boolean().optional(),
        accessKeyId: z.string(),
        accessSecretKey: z.string(),
        ...basicConfig,
    }),
    z.object({
        provider: z.literal(StorageProvider.GoogleStorage),
        projectId: z.string(),
        keyFilename: z.string(),
        bucketName: z.string(),
        ...basicConfig,
    }),
]);
export var StorageProviderKey;
(function (StorageProviderKey) {
    StorageProviderKey["StorageProvider"] = "storageProvider";
    StorageProviderKey["ExperienceBlobsProvider"] = "experienceBlobsProvider";
    StorageProviderKey["ExperienceZipsProvider"] = "experienceZipsProvider";
})(StorageProviderKey || (StorageProviderKey = {}));
export const storageProviderGuard = Object.freeze({
    [StorageProviderKey.StorageProvider]: storageProviderDataGuard,
    [StorageProviderKey.ExperienceBlobsProvider]: storageProviderDataGuard,
    [StorageProviderKey.ExperienceZipsProvider]: storageProviderDataGuard,
});
// Email service provider
export var EmailServiceProvider;
(function (EmailServiceProvider) {
    EmailServiceProvider["LocalOutbox"] = "LocalOutbox";
    EmailServiceProvider["Smtp"] = "Smtp";
    EmailServiceProvider["SendGrid"] = "SendGrid";
    EmailServiceProvider["Cloudflare"] = "Cloudflare";
})(EmailServiceProvider || (EmailServiceProvider = {}));
export const localOutboxEmailServiceConfigGuard = z.object({
    provider: z.literal(EmailServiceProvider.LocalOutbox),
    fromName: z.string(),
    fromEmail: z.string().email(),
});
export const smtpEmailServiceConfigGuard = z.object({
    provider: z.literal(EmailServiceProvider.Smtp),
    host: z.string(),
    port: z.number().int().positive(),
    secure: z.boolean(),
    user: z.string().optional(),
    password: z.string().optional(),
    fromName: z.string(),
    fromEmail: z.string().email(),
    replyTo: z.string().email().optional(),
});
export const sendgridEmailServiceConfigGuard = z.object({
    provider: z.literal(EmailServiceProvider.SendGrid),
    apiKey: z.string(),
    templateId: z.string().optional(),
    fromName: z.string(),
    fromEmail: z.string(),
});
export const cloudflareEmailServiceConfigGuard = z.object({
    provider: z.literal(EmailServiceProvider.Cloudflare),
    apiKey: z.string(),
    accountId: z.string(),
    fromName: z.string(),
    fromEmail: z.string(),
});
export const emailServiceConfigGuard = z.discriminatedUnion('provider', [
    localOutboxEmailServiceConfigGuard,
    smtpEmailServiceConfigGuard,
    sendgridEmailServiceConfigGuard,
    cloudflareEmailServiceConfigGuard,
]);
export var EmailServiceProviderKey;
(function (EmailServiceProviderKey) {
    EmailServiceProviderKey["EmailServiceProvider"] = "emailServiceProvider";
})(EmailServiceProviderKey || (EmailServiceProviderKey = {}));
export const emailServiceProviderGuard = Object.freeze({
    [EmailServiceProviderKey.EmailServiceProvider]: emailServiceConfigGuard,
});
// Self-hosted platform branding
export const platformBrandingConfigGuard = z.object({
    productName: z.string().trim().min(1).max(64),
    slogan: z.string().trim().max(160),
    logoUrl: z
        .string()
        .max(2048)
        .refine((value) => value.startsWith('/') || z.string().url().safeParse(value).success)
        .optional(),
    darkLogoUrl: z
        .string()
        .max(2048)
        .refine((value) => value.startsWith('/') || z.string().url().safeParse(value).success)
        .optional(),
    hideOpenSourceNotice: z.boolean(),
});
export var PlatformBrandingKey;
(function (PlatformBrandingKey) {
    PlatformBrandingKey["PlatformBranding"] = "platformBranding";
})(PlatformBrandingKey || (PlatformBrandingKey = {}));
export const platformBrandingGuard = Object.freeze({
    [PlatformBrandingKey.PlatformBranding]: platformBrandingConfigGuard,
});
// Demo social connectors
export var DemoSocialProvider;
(function (DemoSocialProvider) {
    DemoSocialProvider["Google"] = "google";
    DemoSocialProvider["GitHub"] = "github";
    DemoSocialProvider["Discord"] = "discord";
})(DemoSocialProvider || (DemoSocialProvider = {}));
export const demoSocialDataGuard = z
    .object({
    name: z.string(),
    logo: z.string(),
    logoDark: z.string(),
    provider: z.nativeEnum(DemoSocialProvider),
    clientId: z.string(),
})
    .array();
export var DemoSocialKey;
(function (DemoSocialKey) {
    DemoSocialKey["DemoSocial"] = "demoSocial";
})(DemoSocialKey || (DemoSocialKey = {}));
export const demoSocialGuard = Object.freeze({
    [DemoSocialKey.DemoSocial]: demoSocialDataGuard,
});
// Cloudflare Hostnames
export const hostnameProviderDataGuard = z.object({
    zoneId: z.string(),
    apiToken: z.string(), // Requires zone permission for "SSL and Certificates Edit"
    blockedDomains: z.string().array().optional(), // Optional list of blocked domains
});
// Cloudflare KV for protected app config
export const protectedAppConfigProviderDataGuard = z.object({
    /* Cloudflare Workers & Pages account ID */
    accountIdentifier: z.string(),
    /* KV namespace ID */
    namespaceIdentifier: z.string(),
    /* Key prefix for protected app config */
    keyName: z.string(),
    /* The default domain (e.g protected.app) for the protected app */
    domain: z.string(),
    apiToken: z.string(), // Requires account permission for "KV Storage Edit"
});
/**
 * Cloudflare workers config for custom JWT.
 * Ref: https://developers.cloudflare.com/api/
 */
export const customJwtWorkerConfigGuard = z.object({
    /** Cloudflare API Key (api_key). */
    apiKey: z.string(),
    /** Cloudflare API Key (api_email). */
    apiEmail: z.string(),
    /** Cloudflare account ID. */
    accountId: z.string(),
    /** Default Cloudflare subdomain for the account. */
    subdomain: z.string(),
});
export var CloudflareKey;
(function (CloudflareKey) {
    CloudflareKey["HostnameProvider"] = "cloudflareHostnameProvider";
    CloudflareKey["ProtectedAppConfigProvider"] = "cloudflareProtectedAppConfigProvider";
    CloudflareKey["ProtectedAppHostnameProvider"] = "cloudflareProtectedAppHostnameProvider";
    CloudflareKey["CustomJwtWorkerConfig"] = "cloudflareCustomJwtWorkerConfig";
})(CloudflareKey || (CloudflareKey = {}));
export const cloudflareGuard = Object.freeze({
    [CloudflareKey.HostnameProvider]: hostnameProviderDataGuard,
    [CloudflareKey.ProtectedAppConfigProvider]: protectedAppConfigProviderDataGuard,
    [CloudflareKey.ProtectedAppHostnameProvider]: hostnameProviderDataGuard,
    [CloudflareKey.CustomJwtWorkerConfig]: customJwtWorkerConfigGuard,
});
export const systemKeys = Object.freeze([
    ...Object.values(AlterationStateKey),
    ...Object.values(StorageProviderKey),
    ...Object.values(DemoSocialKey),
    ...Object.values(CloudflareKey),
    ...Object.values(EmailServiceProviderKey),
    ...Object.values(PlatformBrandingKey),
]);
export const systemGuards = Object.freeze({
    ...alterationStateGuard,
    ...storageProviderGuard,
    ...demoSocialGuard,
    ...cloudflareGuard,
    ...emailServiceProviderGuard,
    ...platformBrandingGuard,
});
